/**
 * MatchHistory - Historial de partidas online
 * 501 Dart League Web
 * 
 * Muestra una lista paginada de las partidas online jugadas por el usuario.
 * Cada card muestra: rival, resultado (legs), media, 180s, fecha.
 * Click en una card expande el detalle con stats comparadas.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from './api';
import DashboardHeader from './DashboardHeader';
import { getCheckoutOptions } from './utils/checkouts';
import './styles/match-history.css';
import './styles/index.css';

const MATCHES_PER_PAGE = 10;

const MatchHistory = ({ onBack }) => {
  const [user] = useState(api.getCurrentUser());
  const [matches, setMatches] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartRef = useRef(null);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const [historyData, statsData, trendData] = await Promise.all([
        api.getOnlineMatchHistory(page, MATCHES_PER_PAGE),
        page === 1 ? api.getOnlineStats() : Promise.resolve(null),
        page === 1 ? api.getOnlineDailyTrend() : Promise.resolve(null),
      ]);

      setMatches(historyData.matches);
      setPagination(historyData.pagination);
      if (statsData) setStats(statsData);
      if (page === 1) {
        setDailyTrend(Array.isArray(trendData?.days) ? trendData.days : []);
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const renderTrendChart = () => {
    if (!dailyTrend || dailyTrend.length === 0) {
      return (
        <div className="match-history-trend-empty">
          Juega algunas partidas online para ver la evolución diaria de tu media.
        </div>
      );
    }

    const width = 960;
    const height = 400;
    const paddingX = 72;
    const paddingY = 52;
    const paddingBottom = 60;
    const innerWidth = width - paddingX * 2;
    const innerHeight = height - paddingY - paddingBottom;

    const formatDayShort = (dayStr) => {
      const [year, month, day] = String(dayStr || '').split('-').map(Number);
      if (!year || !month || !day) return dayStr || '';
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    };

    const getWeekStart = (dayStr) => {
      const [year, month, day] = String(dayStr || '').split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      const dayOfWeek = date.getUTCDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      date.setUTCDate(date.getUTCDate() + diffToMonday);
      return date;
    };

    const toIsoDay = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const DAYS_TO_WEEKLY = 70;
    const useWeekly = dailyTrend.length > DAYS_TO_WEEKLY;

    const groupedTrend = useWeekly
      ? (() => {
          const map = new Map();
          dailyTrend.forEach((item) => {
            const weekStartDate = getWeekStart(item.day);
            const weekStart = toIsoDay(weekStartDate);
            const weekEndDate = new Date(weekStartDate);
            weekEndDate.setUTCDate(weekEndDate.getUTCDate() + 6);
            const weekEnd = toIsoDay(weekEndDate);

            if (!map.has(weekStart)) {
              map.set(weekStart, {
                day: weekStart,
                periodStart: weekStart,
                periodEnd: weekEnd,
                matches: 0,
                weightedAvgSum: 0,
                daysPlayed: 0,
              });
            }

            const bucket = map.get(weekStart);
            const matches = Number(item.matches) || 0;
            const avg = Number(item.average) || 0;
            bucket.matches += matches;
            bucket.weightedAvgSum += avg * Math.max(1, matches);
            bucket.daysPlayed += 1;
          });

          return Array.from(map.values())
            .sort((a, b) => a.day.localeCompare(b.day))
            .map((bucket) => ({
              day: bucket.day,
              periodStart: bucket.periodStart,
              periodEnd: bucket.periodEnd,
              daysPlayed: bucket.daysPlayed,
              matches: bucket.matches,
              average: bucket.matches > 0
                ? bucket.weightedAvgSum / bucket.matches
                : 0,
            }));
        })()
      : dailyTrend.map((item) => ({ ...item, periodStart: item.day, periodEnd: item.day, daysPlayed: 1 }));

    const trendPointsData = groupedTrend;

    const values = trendPointsData.map((p) => Number(p.average) || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const rawSpread = maxValue - minValue;
    const rangePadding = rawSpread > 0 ? Math.max(1.5, rawSpread * 0.12) : 4;
    const chartMin = Math.max(0, minValue - rangePadding);
    const chartMax = maxValue + rangePadding;
    const spread = chartMax - chartMin || 1;

    const points = trendPointsData.map((point, index) => {
      const x = trendPointsData.length === 1
        ? width / 2
        : paddingX + (index / (trendPointsData.length - 1)) * innerWidth;
      const y = paddingY + ((chartMax - (Number(point.average) || 0)) / spread) * innerHeight;
      return {
        x,
        y,
        index,
        day: point.day,
        periodStart: point.periodStart,
        periodEnd: point.periodEnd,
        daysPlayed: point.daysPlayed,
        average: Number(point.average) || 0,
        matches: point.matches || 0,
      };
    });

    // Smooth curve path using cardinal spline
    const buildSmoothPath = (pts) => {
      if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`;
      if (pts.length === 2) return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }
      return d;
    };

    const path = buildSmoothPath(points);
    const areaPath = path + ` L ${points[points.length - 1].x} ${height - paddingBottom} L ${points[0].x} ${height - paddingBottom} Z`;

    const yTickCount = 5;
    const yTicks = Array.from({ length: yTickCount }, (_, i) => {
      const ratio = i / (yTickCount - 1);
      return {
        y: paddingY + ratio * innerHeight,
        value: chartMax - ratio * spread,
      };
    });

    // X-axis labels: show ~6-8 evenly spaced
    const maxXLabels = Math.min(8, trendPointsData.length);
    const xLabelStep = Math.max(1, Math.floor((trendPointsData.length - 1) / (maxXLabels - 1)));
    const xLabels = [];
    for (let i = 0; i < trendPointsData.length; i += xLabelStep) {
      xLabels.push({ index: i, label: formatDayShort(trendPointsData[i]?.periodStart || ''), x: points[i].x });
    }
    if (xLabels[xLabels.length - 1]?.index !== trendPointsData.length - 1) {
      xLabels.push({ index: trendPointsData.length - 1, label: formatDayShort(trendPointsData[trendPointsData.length - 1]?.periodStart || ''), x: points[points.length - 1].x });
    }

    const lastPoint = points[points.length - 1];
    const bestIdx = values.indexOf(maxValue);
    const worstIdx = values.indexOf(minValue);

    const handleChartMouseMove = (e) => {
      if (!chartRef.current || points.length === 0) return;
      const rect = chartRef.current.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * width;
      let closest = points[0];
      let minDist = Math.abs(svgX - closest.x);
      for (const p of points) {
        const dist = Math.abs(svgX - p.x);
        if (dist < minDist) { minDist = dist; closest = p; }
      }
      setHoveredPoint(closest);
    };

    const handleChartMouseLeave = () => setHoveredPoint(null);

    return (
      <div className="match-history-trend-card">
        <div className="match-history-trend-header">
          <h3>{useWeekly ? 'Tu media semanal (últimos 3 meses)' : 'Tu media diaria (últimos 3 meses)'}</h3>
          <span>
            {useWeekly
              ? `${trendPointsData.length} semana${trendPointsData.length !== 1 ? 's' : ''} (${dailyTrend.length} días jugados)`
              : `${dailyTrend.length} día${dailyTrend.length !== 1 ? 's' : ''} jugado${dailyTrend.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="match-history-trend-metrics">
          <div className="trend-metric-item">
            <span className="trend-metric-label">Última</span>
            <span className="trend-metric-value">{lastPoint.average.toFixed(1)}</span>
          </div>
          <div className="trend-metric-item">
            <span className="trend-metric-label">{useWeekly ? 'Mejor semana' : 'Mejor día'}</span>
            <span className="trend-metric-value">{maxValue.toFixed(1)}</span>
          </div>
          <div className="trend-metric-item">
            <span className="trend-metric-label">{useWeekly ? 'Peor semana' : 'Peor día'}</span>
            <span className="trend-metric-value">{minValue.toFixed(1)}</span>
          </div>
        </div>

        <div className="match-history-trend-chart-wrap" onMouseMove={handleChartMouseMove} onMouseLeave={handleChartMouseLeave}>
          <svg ref={chartRef} viewBox={`0 0 ${width} ${height}`} className="match-history-trend-chart" role="img" aria-label="Evolución diaria de media online">
            <defs>
              <linearGradient id="trendAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00ffaa" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#00ffaa" stopOpacity="0.02" />
              </linearGradient>
              <filter id="glowDot" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="glow" />
                <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Grid lines */}
            {yTicks.map((tick, idx) => (
              <line key={`grid-${idx}`} x1={paddingX} y1={tick.y} x2={width - paddingX} y2={tick.y} className="trend-grid" />
            ))}

            {/* Y axis labels */}
            {yTicks.map((tick, idx) => (
              <text key={`y-${idx}`} x={paddingX - 12} y={tick.y + 4} textAnchor="end" className="trend-y-label-new">
                {tick.value.toFixed(1)}
              </text>
            ))}

            {/* X axis labels */}
            {xLabels.map((lbl, idx) => (
              <text key={`x-${idx}`} x={lbl.x} y={height - paddingBottom + 28} textAnchor="middle" className="trend-x-label">
                {lbl.label}
              </text>
            ))}

            {/* Axes */}
            <line x1={paddingX} y1={paddingY} x2={paddingX} y2={height - paddingBottom} className="trend-axis" />
            <line x1={paddingX} y1={height - paddingBottom} x2={width - paddingX} y2={height - paddingBottom} className="trend-axis" />

            {/* Area fill */}
            <path d={areaPath} fill="url(#trendAreaGrad)" />

            {/* Line */}
            <path d={path} className="trend-line-new" />

            {/* Key points: best, worst, last */}
            {[bestIdx, worstIdx].filter((v, i, a) => a.indexOf(v) === i).map((idx) => (
              <g key={`key-${idx}`}>
                <circle cx={points[idx].x} cy={points[idx].y} r="6" className={idx === bestIdx ? 'trend-key-point best' : 'trend-key-point worst'} />
                <text x={points[idx].x} y={points[idx].y - 14} textAnchor="middle" className={idx === bestIdx ? 'trend-key-label best' : 'trend-key-label worst'}>
                  {points[idx].average.toFixed(1)}
                </text>
              </g>
            ))}

            {/* Last point highlight */}
            <circle cx={lastPoint.x} cy={lastPoint.y} r="7" className="trend-key-point last" filter="url(#glowDot)" />
            <text x={lastPoint.x} y={lastPoint.y - 14} textAnchor="middle" className="trend-key-label last">
              {lastPoint.average.toFixed(1)}
            </text>

            {/* All data points */}
            {points.map((point) => (
              <circle key={point.day} cx={point.x} cy={point.y} r="4.5" className="trend-point-new" />
            ))}

            {/* Hover vertical line + enlarged point */}
            {hoveredPoint && (
              <g className="trend-hover-group">
                <line x1={hoveredPoint.x} y1={paddingY} x2={hoveredPoint.x} y2={height - paddingBottom} className="trend-hover-line" />
                <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="7" className="trend-hover-dot" />
              </g>
            )}
          </svg>

          {/* HTML Tooltip overlay */}
          {hoveredPoint && (
            <div
              className="trend-tooltip"
              style={{
                left: `${(hoveredPoint.x / width) * 100}%`,
                top: `${(hoveredPoint.y / height) * 100}%`,
              }}
            >
              <div className="trend-tooltip-inner">
                <span className="trend-tooltip-avg">{hoveredPoint.average.toFixed(1)}</span>
                <span className="trend-tooltip-date">
                  {useWeekly
                    ? `${formatDayShort(hoveredPoint.periodStart)} - ${formatDayShort(hoveredPoint.periodEnd)}`
                    : formatDayShort(hoveredPoint.day)}
                </span>
                <span className="trend-tooltip-matches">
                  {hoveredPoint.matches} partida{hoveredPoint.matches !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  const handlePageChange = (newPage) => {
    setExpandedId(null);
    loadData(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) return 'Hace un momento';
    if (diffHours < 24) return `Hace ${Math.floor(diffHours)}h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;

    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getMatchInfo = (match) => {
    const userId = user._id;
    const isPlayer1 = match.player1 === userId;
    const won = match.winner === userId;
    const rivalName = isPlayer1 ? match.player2Username : match.player1Username;
    const myLegs = isPlayer1 ? match.legsWon.player1 : match.legsWon.player2;
    const rivalLegs = isPlayer1 ? match.legsWon.player2 : match.legsWon.player1;
    const myStats = isPlayer1 ? match.stats?.player1 : match.stats?.player2;
    const rivalStats = isPlayer1 ? match.stats?.player2 : match.stats?.player1;

    const legHistory = Array.isArray(match.legHistory) ? match.legHistory : [];
    const gameMode = match.gameMode || 501;

    const calcAdvancedFromRounds = (rounds = []) => {
      let scoreBeforeTurn = gameMode;
      let count100Plus = 0;
      let count140Plus = 0;
      let checkoutOpportunities = 0;
      let checkoutSuccess = 0;
      let highFinish = 0;

      rounds.forEach((round) => {
        if (getCheckoutOptions(scoreBeforeTurn).length > 0) {
          checkoutOpportunities += 1;
        }

        const score = Number(round?.score);
        const isScoreValid = Number.isFinite(score);
        const isBustOrTimeout = Boolean(round?.bust || round?.timeout);

        if (round?.closing && isScoreValid) {
          checkoutSuccess += 1;
          highFinish = Math.max(highFinish, score);
          scoreBeforeTurn = 0;
          return;
        }

        if (!isScoreValid || isBustOrTimeout) return;

        if (score >= 100) count100Plus += 1;
        if (score >= 140) count140Plus += 1;
        scoreBeforeTurn = Math.max(0, scoreBeforeTurn - score);
      });

      return { count100Plus, count140Plus, checkoutOpportunities, checkoutSuccess, highFinish };
    };

    const myAdvanced = { count100Plus: 0, count140Plus: 0, checkoutOpportunities: 0, checkoutSuccess: 0, highFinish: 0 };
    const rivalAdvanced = { count100Plus: 0, count140Plus: 0, checkoutOpportunities: 0, checkoutSuccess: 0, highFinish: 0 };

    legHistory.forEach((leg) => {
      const myRounds = isPlayer1 ? (leg.player1Rounds || []) : (leg.player2Rounds || []);
      const rivalRounds = isPlayer1 ? (leg.player2Rounds || []) : (leg.player1Rounds || []);

      const mine = calcAdvancedFromRounds(myRounds);
      const rival = calcAdvancedFromRounds(rivalRounds);

      myAdvanced.count100Plus += mine.count100Plus;
      myAdvanced.count140Plus += mine.count140Plus;
      myAdvanced.checkoutOpportunities += mine.checkoutOpportunities;
      myAdvanced.checkoutSuccess += mine.checkoutSuccess;
      myAdvanced.highFinish = Math.max(myAdvanced.highFinish, mine.highFinish);

      rivalAdvanced.count100Plus += rival.count100Plus;
      rivalAdvanced.count140Plus += rival.count140Plus;
      rivalAdvanced.checkoutOpportunities += rival.checkoutOpportunities;
      rivalAdvanced.checkoutSuccess += rival.checkoutSuccess;
      rivalAdvanced.highFinish = Math.max(rivalAdvanced.highFinish, rival.highFinish);
    });

    myAdvanced.checkoutPct = myAdvanced.checkoutOpportunities > 0
      ? (myAdvanced.checkoutSuccess / myAdvanced.checkoutOpportunities) * 100
      : 0;

    rivalAdvanced.checkoutPct = rivalAdvanced.checkoutOpportunities > 0
      ? (rivalAdvanced.checkoutSuccess / rivalAdvanced.checkoutOpportunities) * 100
      : 0;

    return { won, rivalName, myLegs, rivalLegs, myStats, rivalStats, myAdvanced, rivalAdvanced };
  };

  return (
    <div className="dart-league-dashboard training-menu-container">
      <DashboardHeader
        user={user}
        subtitle="Historial de Partidas"
        promedioOnline={stats?.promedioOnline ?? null}
        porcentajeVictoriasOnline={stats?.porcentajeVictorias ?? null}
        showStats={stats?.promedioOnline != null}
        onBack={onBack}
      />

      <main style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: '40px' }}>
        {/* Stats resumen */}
        {stats && stats.totalPartidas > 0 && (
          <div className="match-history-summary">
            <div className="match-summary-item">
              <span className="match-summary-value">{stats.totalPartidas}</span>
              <span className="match-summary-label">Partidas</span>
            </div>
            <div className="match-summary-item">
              <span className="match-summary-value">{stats.partidasGanadas}</span>
              <span className="match-summary-label">Victorias</span>
            </div>
            <div className="match-summary-item">
              <span className="match-summary-value">{stats.promedioOnline?.toFixed(1) || '-'}</span>
              <span className="match-summary-label">Media</span>
            </div>
            <div className="match-summary-item">
              <span className="match-summary-value">{stats.total180s}</span>
              <span className="match-summary-label">180s</span>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="match-history-loading">
            <div className="online-loading-spinner"></div>
            <p>Cargando historial...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && matches.length === 0 && (
          <div className="match-history-empty">
            <img src="/Logo26.png" alt="501 Dart League" style={{width: '80px', height: '80px', opacity: 0.7, marginBottom: '12px'}} />
            <h3>Sin partidas online aún</h3>
            <p>Juega tu primera partida online para que aparezca aquí.</p>
          </div>
        )}

        {/* Lista de partidas */}
        {!loading && matches.length > 0 && (
          <div className="match-history-content-layout-v2">
            {/* Chart full-width */}
            <section className="match-history-chart-section">
              {renderTrendChart()}
            </section>

            {/* Indicador de partidas */}
            <div className="match-history-count-bar">
              <span>Mostrando {matches.length} de {pagination.total || matches.length} partidas</span>
            </div>

            <section className="match-history-main-column-v2">
              <div className="match-history-list">
                {matches.map((match) => {
                const { won, rivalName, myLegs, rivalLegs, myStats, rivalStats, myAdvanced, rivalAdvanced } = getMatchInfo(match);
                const isExpanded = expandedId === match._id;
                const myAvg = myStats?.totalDarts > 0
                  ? ((myStats.totalScore / myStats.totalDarts) * 3).toFixed(1)
                  : '-';
                const rivalAvg = rivalStats?.totalDarts > 0
                  ? ((rivalStats.totalScore / rivalStats.totalDarts) * 3).toFixed(1)
                  : '-';

                return (
                  <div
                    key={match._id}
                    className={`match-history-card ${won ? 'win' : 'loss'} ${match.forfeit ? 'forfeit' : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : match._id)}
                  >
                    {/* Header: resultado + fecha */}
                    <div className="match-history-header">
                      <span className={`match-history-result ${won ? 'win' : 'loss'}`}>
                        {won ? '✓ Victoria' : '✗ Derrota'}
                      </span>
                      <span className="match-history-date">
                        {match.gameMode} · Bo{match.legsToWin * 2 - 1} · {formatDate(match.finishedAt)}
                      </span>
                    </div>

                    {/* Versus */}
                    <div className="match-history-versus">
                      <span className="match-history-player me">{user.username}</span>
                      <span className="match-history-score">{myLegs} - {rivalLegs}</span>
                      <span className="match-history-player rival">{rivalName}</span>
                    </div>

                    {/* Stats rápidas */}
                    <div className="match-history-stats">
                      <div className="match-history-stat">
                        <span className="stat-label">Media:</span>
                        <span className="stat-value">{myAvg}</span>
                      </div>
                      <div className="match-history-stat">
                        <span className="stat-label">180s:</span>
                        <span className="stat-value">{myStats?.count180s || 0}</span>
                      </div>
                    </div>

                    {/* Forfeit badge */}
                    {match.forfeit && (
                      <div className="match-history-forfeit">
                        ⚠️ {match.forfeitReason === 'disconnect' ? 'Desconexión' : 'Abandono'}
                      </div>
                    )}

                    {/* Detalle expandido */}
                    {isExpanded && (
                      <div className="match-history-detail">
                        <div className="match-detail-grid">
                          {/* Header */}
                          <div className="match-detail-label">Tú</div>
                          <div className="match-detail-label"></div>
                          <div className="match-detail-label">Rival</div>

                          {/* Media */}
                          <div className="match-detail-row">
                            <span className="me-val">{myAvg}</span>
                            <span className="detail-label">Media</span>
                            <span className="rival-val">{rivalAvg}</span>
                          </div>

                          {/* 100+ */}
                          <div className="match-detail-row">
                            <span className="me-val">{myAdvanced.count100Plus}</span>
                            <span className="detail-label">100+</span>
                            <span className="rival-val">{rivalAdvanced.count100Plus}</span>
                          </div>

                          {/* 140+ */}
                          <div className="match-detail-row">
                            <span className="me-val">{myAdvanced.count140Plus}</span>
                            <span className="detail-label">140+</span>
                            <span className="rival-val">{rivalAdvanced.count140Plus}</span>
                          </div>

                          {/* Checkout % */}
                          <div className="match-detail-row">
                            <span className="me-val">{myAdvanced.checkoutPct.toFixed(1)}%</span>
                            <span className="detail-label">Checkout % (salida real)</span>
                            <span className="rival-val">{rivalAdvanced.checkoutPct.toFixed(1)}%</span>
                          </div>

                          {/* Cierre alto */}
                          <div className="match-detail-row">
                            <span className="me-val">{myAdvanced.highFinish || '-'}</span>
                            <span className="detail-label">Cierre alto</span>
                            <span className="rival-val">{rivalAdvanced.highFinish || '-'}</span>
                          </div>

                          {/* 180s */}
                          <div className="match-detail-row">
                            <span className="me-val">{myStats?.count180s || 0}</span>
                            <span className="detail-label">180s</span>
                            <span className="rival-val">{rivalStats?.count180s || 0}</span>
                          </div>

                          {/* Legs */}
                          <div className="match-detail-row">
                            <span className="me-val">{myLegs}</span>
                            <span className="detail-label">Legs</span>
                            <span className="rival-val">{rivalLegs}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>

              {/* Paginación profesional */}
              {pagination.totalPages > 1 && (
                <div className="match-history-pagination-v2">
                  <button
                    className="pagination-btn pagination-arrow"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(1)}
                    title="Primera página"
                  >
                    «
                  </button>
                  <button
                    className="pagination-btn pagination-arrow"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    title="Anterior"
                  >
                    ‹
                  </button>

                  {(() => {
                    const pages = [];
                    const total = pagination.totalPages;
                    const current = pagination.page;
                    let start = Math.max(1, current - 2);
                    let end = Math.min(total, current + 2);
                    if (current <= 3) end = Math.min(total, 5);
                    if (current >= total - 2) start = Math.max(1, total - 4);

                    if (start > 1) {
                      pages.push(<button key={1} className="pagination-btn pagination-num" onClick={() => handlePageChange(1)}>1</button>);
                      if (start > 2) pages.push(<span key="dots-start" className="pagination-dots">…</span>);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`pagination-btn pagination-num ${i === current ? 'active' : ''}`}
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </button>
                      );
                    }
                    if (end < total) {
                      if (end < total - 1) pages.push(<span key="dots-end" className="pagination-dots">…</span>);
                      pages.push(<button key={total} className="pagination-btn pagination-num" onClick={() => handlePageChange(total)}>{total}</button>);
                    }
                    return pages;
                  })()}

                  <button
                    className="pagination-btn pagination-arrow"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    title="Siguiente"
                  >
                    ›
                  </button>
                  <button
                    className="pagination-btn pagination-arrow"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.totalPages)}
                    title="Última página"
                  >
                    »
                  </button>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default MatchHistory;
